import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/apiClient';

function Profile({ user, onLogout }) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Church selection dialog state
  const [selectChurchDialogOpen, setSelectChurchDialogOpen] = useState(false);
  const [myChurches, setMyChurches] = useState([]);
  const [allChurches, setAllChurches] = useState([]);
  const [loadingChurches, setLoadingChurches] = useState(false);
  const [joining, setJoining] = useState(null);
  const [selectedChurch, setSelectedChurch] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          if (user.id) {
            const userData = await apiClient.getCurrentUser(user.id);
            setUserData(userData);
            setName(userData.name || '');

            // Validate selected church for church_user role
            if (userData.role === 'church_user') {
              const stored = localStorage.getItem('selectedChurch');
              if (stored) {
                const storedChurch = JSON.parse(stored);
                const userChurchIds = userData.churchIds || [];
                
                // Only set selected church if user is actually a member of it
                if (userChurchIds.includes(storedChurch.id)) {
                  setSelectedChurch(storedChurch);
                } else {
                  // Clear invalid selected church from localStorage
                  localStorage.removeItem('selectedChurch');
                  setSelectedChurch(null);
                }
              }
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updatedUser = await apiClient.updateUser(userData.id, { name });
      setUserData(updatedUser);
      setSuccess('Profile updated successfully');
      
      // Update user in localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        userObj.name = updatedUser.name;
        localStorage.setItem('user', JSON.stringify(userObj));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSelectChurch = async () => {
    setSelectChurchDialogOpen(true);
    setLoadingChurches(true);
    try {
      const [all, my] = await Promise.all([
        apiClient.getAllChurches(),
        apiClient.getMyChurches(user.id)
      ]);
      setAllChurches(all);
      setMyChurches(my);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingChurches(false);
    }
  };

  const handleCloseSelectChurch = () => {
    setSelectChurchDialogOpen(false);
  };

  const handleSelectChurch = (church) => {
    // Verify the church is in user's churchIds before selecting
    const userChurchIds = userData?.churchIds || [];
    if (!userChurchIds.includes(church.id)) {
      setError('You are not a member of this church');
      return;
    }
    
    localStorage.setItem('selectedChurch', JSON.stringify(church));
    setSelectedChurch(church);
    setSelectChurchDialogOpen(false);
    setSuccess('Church selected successfully');
    
    // Dispatch event to update header
    window.dispatchEvent(new Event('churchSelected'));
  };

  const handleJoinChurch = async (churchId) => {
    setJoining(churchId);
    try {
      await apiClient.joinChurch(churchId, user.id);
      
      // Refresh user data to get updated churchIds
      const updatedUser = await apiClient.getCurrentUser(user.id);
      setUserData(updatedUser);
      
      const my = await apiClient.getMyChurches(user.id);
      setMyChurches(my);
      
      // Auto-select the newly joined church
      const joinedChurch = my.find(c => c.id === churchId);
      if (joinedChurch) {
        localStorage.setItem('selectedChurch', JSON.stringify(joinedChurch));
        setSelectedChurch(joinedChurch);
        window.dispatchEvent(new Event('churchSelected'));
      }
      
      // Close dialog and navigate to dashboard
      setSelectChurchDialogOpen(false);
      navigate('/church-user/dashboard');
    } catch (err) {
      alert(err.message);
    } finally {
      setJoining(null);
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const myChurchIds = myChurches.map(c => c.id);
  const availableChurches = allChurches.filter(c => !myChurchIds.includes(c.id));

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        My Profile
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, maxWidth: { xs: '100%', md: 600 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <TextField
            fullWidth
            label="Phone Number"
            value={userData?.phoneNumber || ''}
            disabled
          />

          {/* Church Selection for church_user role */}
          {user?.role === 'church_user' && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Selected Church
                </Typography>
                {selectedChurch ? (
                  <Card sx={{ mt: 1 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={selectedChurch.logoUrl} sx={{ width: 60, height: 60 }}>
                          {selectedChurch.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{selectedChurch.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedChurch.address?.city}, {selectedChurch.address?.state}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No church selected. Please select a church to continue.
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenSelectChurch}
                  sx={{ mt: 2 }}
                >
                  {selectedChurch ? 'Change Church' : 'Select Church'}
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !name.trim()}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ flex: 1 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              onClick={handleLogout}
              sx={{ flex: 1 }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Church Selection Dialog */}
      <Dialog
        open={selectChurchDialogOpen}
        onClose={handleCloseSelectChurch}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Church</DialogTitle>
        <DialogContent>
          {loadingChurches ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {myChurches.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You haven't joined any churches yet. Browse available churches below.
                </Alert>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    My Churches
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {myChurches.map((church) => (
                      <Grid item xs={12} sm={6} md={4} key={church.id}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Avatar src={church.logoUrl} sx={{ width: 50, height: 50 }}>
                                {church.name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">{church.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {church.address?.city}, {church.address?.state}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                          <CardActions>
                            <Button
                              variant="contained"
                              fullWidth
                              size="small"
                              onClick={() => handleSelectChurch(church)}
                            >
                              Select
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {availableChurches.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Available Churches
                  </Typography>
                  <Grid container spacing={2}>
                    {availableChurches.map((church) => (
                      <Grid item xs={12} sm={6} md={4} key={church.id}>
                        <Card>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Avatar src={church.logoUrl} sx={{ width: 50, height: 50 }}>
                                {church.name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">{church.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {church.address?.city}, {church.address?.state}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                          <CardActions>
                            <Button
                              variant="outlined"
                              fullWidth
                              size="small"
                              onClick={() => handleJoinChurch(church.id)}
                              disabled={joining === church.id}
                            >
                              {joining === church.id ? <CircularProgress size={20} /> : 'Join Church'}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSelectChurch}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;

